import { ClimateApiErrorType } from './types';

/**
 * Custom error class for Climate API operations
 */
export class ClimateApiError extends Error {
  public readonly type: ClimateApiErrorType;
  public readonly statusCode?: number;
  public readonly originalError?: unknown;
  public readonly correlationId?: string;

  constructor(
    type: ClimateApiErrorType,
    message: string,
    statusCode?: number,
    originalError?: unknown,
    correlationId?: string
  ) {
    super(message);
    this.name = 'ClimateApiError';
    this.type = type;
    this.statusCode = statusCode;
    this.originalError = originalError;
    this.correlationId = correlationId;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ClimateApiError);
    }
  }

  /**
   * Create error from fetch response
   */
  static async fromResponse(
    response: Response,
    correlationId?: string
  ): Promise<ClimateApiError> {
    let errorData: { error?: { message?: string }; message?: string } = {};
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: 'Unable to parse error response' };
    }

    const message = errorData.error?.message || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
    
    // Map HTTP status codes to error types
    let type: ClimateApiErrorType;
    switch (response.status) {
      case 400:
        type = ClimateApiErrorType.VALIDATION_ERROR;
        break;
      case 401:
      case 403:
        type = ClimateApiErrorType.UNAUTHORIZED_ERROR;
        break;
      case 404:
        type = ClimateApiErrorType.NOT_FOUND_ERROR;
        break;
      case 408:
        type = ClimateApiErrorType.TIMEOUT_ERROR;
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        type = ClimateApiErrorType.SERVER_ERROR;
        break;
      default:
        type = ClimateApiErrorType.SERVER_ERROR;
    }

    return new ClimateApiError(
      type,
      message,
      response.status,
      errorData,
      correlationId
    );
  }

  /**
   * Create error from network/fetch failure
   */
  static fromNetworkError(
    error: Error,
    correlationId?: string
  ): ClimateApiError {
    const isTimeoutError = error.name === 'AbortError' || error.message.includes('timeout');
    
    return new ClimateApiError(
      isTimeoutError ? ClimateApiErrorType.TIMEOUT_ERROR : ClimateApiErrorType.NETWORK_ERROR,
      isTimeoutError ? 'Request timed out' : `Network error: ${error.message}`,
      undefined,
      error,
      correlationId
    );
  }

  /**
   * Get user-friendly error message
   */
  getUserMessage(): string {
    switch (this.type) {
      case ClimateApiErrorType.NETWORK_ERROR:
        return 'Unable to connect to climate server. Please check your internet connection.';
      case ClimateApiErrorType.TIMEOUT_ERROR:
        return 'Request timed out. The climate server may be busy.';
      case ClimateApiErrorType.VALIDATION_ERROR:
        return this.message || 'Invalid data provided.';
      case ClimateApiErrorType.UNAUTHORIZED_ERROR:
        return 'Access denied. Please check your credentials.';
      case ClimateApiErrorType.NOT_FOUND_ERROR:
        return 'Requested resource not found.';
      case ClimateApiErrorType.SERVER_ERROR:
        return 'Climate server error. Please try again later.';
      default:
        return 'An unexpected error occurred.';
    }
  }

  /**
   * Check if error is retryable
   */
  isRetryable(): boolean {
    return this.type === ClimateApiErrorType.NETWORK_ERROR || 
           this.type === ClimateApiErrorType.TIMEOUT_ERROR ||
           (this.type === ClimateApiErrorType.SERVER_ERROR && 
            this.statusCode !== undefined && this.statusCode >= 500);
  }
} 